import { Fragment, type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

// A single delta chip vs a named period. `goodWhenUp` decides which direction
// reads as good, so the color encodes direction × goodness (the standard
// stat-tile delta), independent of the card's severity flag.
//
// `format` picks how `pct` is read: 'percent' (default) is a percent change of a
// count/amount and renders as "▲12%"; 'points' is a percentage-POINT difference
// of two rates and renders as "▲3.2pp" — a rate that moved from 40% to 43.2% is
// +3.2pp, not +8%, so rate metrics must use 'points' to stay honest.
export type KpiStatDelta = {
    readonly label: string;
    readonly pct: number | null;
    readonly goodWhenUp: boolean;
    readonly format?: 'percent' | 'points';
};

// Optional severity flag on the whole card. Only amber/red exist — the absence
// of a flag is the ok state, so there is no neutral styling to reserve.
export type KpiStatFlag = {
    readonly level: 'amber' | 'red';
    readonly description: string;
};

type KpiStatCardProps = {
    readonly label: string;
    readonly value: string;
    readonly sub?: string;
    readonly flag?: KpiStatFlag;
    readonly deltas?: Array<KpiStatDelta>;
    // Sparkline slot (e.g. a <KpiSparkline>), rendered under the value.
    readonly sparkline?: ReactNode;
    readonly onClick?: () => void;
    readonly compact?: boolean;
    // Smaller value text for values that are phrases rather than one number.
    readonly valueSize?: 'default' | 'sm';
};

const FLAG_CARD_CLASSES: Record<KpiStatFlag['level'], string> = {
    amber: 'border-amber-500/60 bg-amber-500/10',
    red: 'border-red-500/60 bg-red-500/10',
};

const FLAG_VALUE_CLASSES: Record<KpiStatFlag['level'], string> = {
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
};

function DeltaChip({ delta }: { readonly delta: KpiStatDelta }) {
    if (delta.pct === null) {
        return (
            <span className="text-muted-foreground inline-flex items-center gap-0.5 tabular-nums">
                <span className="uppercase">{delta.label}</span>
                <span>—</span>
            </span>
        );
    }
    const isUp = delta.pct >= 0;
    const isGood = isUp === delta.goodWhenUp;
    const magnitude =
        delta.format === 'points'
            ? `${Math.abs(delta.pct).toFixed(1)}pp`
            : `${Math.abs(delta.pct).toFixed(0)}%`;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 tabular-nums',
                isGood
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400',
            )}
        >
            <span className="uppercase">{delta.label}</span>
            <span>
                {isUp ? '▲' : '▼'}
                {magnitude}
            </span>
        </span>
    );
}

export function KpiStatCard({
    label,
    value,
    sub,
    flag,
    deltas,
    sparkline,
    onClick,
    valueSize = 'default',
    compact = false,
}: KpiStatCardProps) {
    const clickable = onClick !== undefined;
    const className = cn(
        'flex h-full w-full flex-col gap-1 rounded-lg border text-left',
        compact ? 'p-3' : 'p-4',
        flag ? FLAG_CARD_CLASSES[flag.level] : '',
        clickable &&
            'hover:border-ring focus-visible:border-ring focus-visible:ring-ring/50 cursor-pointer transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-none',
    );
    const body = (
        <Fragment>
            <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {label}
            </span>
            <span
                className={cn(
                    'font-semibold',
                    valueSize === 'sm'
                        ? 'text-lg'
                        : compact
                          ? 'text-2xl'
                          : 'text-4xl',
                    flag ? FLAG_VALUE_CLASSES[flag.level] : '',
                )}
            >
                {value}
            </span>
            {sub ? (
                <span className="text-muted-foreground text-xs tabular-nums">
                    {sub}
                </span>
            ) : null}
            {flag && flag.description ? (
                <span className={cn('text-xs', FLAG_VALUE_CLASSES[flag.level])}>
                    {flag.description}
                </span>
            ) : null}
            {deltas && deltas.length > 0 ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {deltas.map((delta) => (
                        <DeltaChip key={delta.label} delta={delta} />
                    ))}
                </div>
            ) : null}
            {sparkline ? <div className="mt-2">{sparkline}</div> : null}
        </Fragment>
    );
    if (clickable) {
        return (
            <button type="button" onClick={onClick} className={className}>
                {body}
            </button>
        );
    }
    return <div className={className}>{body}</div>;
}
